/**
 * React Query (TanStack Query) Setup
 * Provides caching, background updates, and automatic refetching for API calls
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Default query client configuration
 * Optimized for performance with sensible defaults
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache time: how long data stays in cache after last use
      cacheTime: 5 * 60 * 1000, // 5 minutes
      
      // Stale time: how long data is considered fresh
      staleTime: 1 * 60 * 1000, // 1 minute
      
      // Retry failed requests
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      
      // Retry delay: exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch on window focus (but respect staleTime)
      refetchOnWindowFocus: true,
      
      // Refetch on reconnect
      refetchOnReconnect: true,
      
      // Don't refetch on mount if data is fresh
      refetchOnMount: true,
      
      // Show loading state immediately
      suspense: false,
      
      // Use error boundary
      useErrorBoundary: false, // Set to true if you want to use error boundaries
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
      
      // Retry delay for mutations
      retryDelay: 1000,
    },
  },
});

/**
 * Query key factory
 * Centralizes query keys for easier cache invalidation
 */
export const queryKeys = {
  // Auth
  auth: {
    user: ['auth', 'user'] as const,
    permissions: ['auth', 'permissions'] as const,
  },
  
  // Reports
  reports: {
    all: ['reports'] as const,
    list: (filters?: any) => ['reports', 'list', filters] as const,
    detail: (id: string | number) => ['reports', 'detail', id] as const,
    batch: {
      all: ['reports', 'batch'] as const,
      detail: (id: string | number) => ['reports', 'batch', id] as const,
    },
  },
  
  // Reservations
  reservations: {
    all: ['reservations'] as const,
    list: (filters?: any) => ['reservations', 'list', filters] as const,
    detail: (id: string | number) => ['reservations', 'detail', id] as const,
  },
  
  // Guests
  guests: {
    all: ['guests'] as const,
    list: (filters?: any) => ['guests', 'list', filters] as const,
    detail: (id: string | number) => ['guests', 'detail', id] as const,
    history: (id: string | number) => ['guests', 'history', id] as const,
  },
  
  // Rooms
  rooms: {
    all: ['rooms'] as const,
    list: (filters?: any) => ['rooms', 'list', filters] as const,
    detail: (id: string | number) => ['rooms', 'detail', id] as const,
    status: (date?: string) => ['rooms', 'status', date] as const,
  },

  // Room Plan
  roomPlan: {
    all: ['roomPlan'] as const,
    resources: ['roomPlan', 'resources'] as const,
    resource: (params?: any) => ['roomPlan', 'resources', params] as const,
    events: ['roomPlan', 'events'] as const,
    event: (params?: any) => ['roomPlan', 'events', params] as const,
  },
  
  // Cashier
  cashier: {
    folio: {
      all: ['cashier', 'folio'] as const,
      detail: (id: string | number) => ['cashier', 'folio', id] as const,
    },
    transactions: (folioId: string | number) => ['cashier', 'transactions', folioId] as const,
  },
  
  // Setup/Lookup data
  lookups: {
    all: ['lookups'] as const,
    city: ['lookups', 'city'] as const,
    country: ['lookups', 'country'] as const,
    roomType: ['lookups', 'roomType'] as const,
    rateCode: ['lookups', 'rateCode'] as const,
    channel: ['lookups', 'channel'] as const,
  },
} as const;
